import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';

import Form from '../ui/forms/index';
import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';
import MuiSelect from '@mui/material/Select';
import YellowButton from '../ui/buttons/yellowButton';
import { useParams } from 'react-router-dom';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateBracket } from '../../features/bracket/bracketSlice';
import { generateTeamName } from '../../helpers/teams';

const schema = yup.object().shape({
  name: yup.string().required(),
  type: yup.string().required(),
  useSections: yup.boolean().default(false)
});

// Dropdown control of players. Needs to dynamically generate based on the number of players in the event. 16 means 8 dropdowns but 15 means also 8 dropdowns vs 8 dropdowns.
// Should be a table Player VS Player.

const PlayerDropdown = ({ teams, matchUps, onChange, onAddMatch, onRemoveMatch }) => (
  <Table size="small" sx={{ my: 2 }}>
    <TableBody>
      {matchUps.map((match, idx) => (
        <TableRow
          key={idx}
          sx={{
            '& td': { borderBottom: 'none' }
          }}>
          <TableCell sx={{ width: '40%' }}>
            <MuiSelect
              fullWidth
              value={match.team1}
              onChange={(e) => onChange(idx, 'team1', e.target.value)}
              displayEmpty
              sx={{ width: '100%' }}>
              <MenuItem value="">Select Team 1</MenuItem>
              {teams
                .filter(
                  (t) =>
                    t.id === match.team1.id ||
                    !matchUps.some(
                      (m, i) => i !== idx && (m.team1.id === t.id || m.team2.id === t.id)
                    )
                )
                .map((t) => (
                  <MenuItem key={t.id} value={t}>
                    {t.name}
                  </MenuItem>
                ))}
            </MuiSelect>
          </TableCell>
          <TableCell sx={{ width: '10%', textAlign: 'center' }}>VS</TableCell>
          <TableCell sx={{ width: '40%' }}>
            <MuiSelect
              fullWidth
              value={match.team2}
              onChange={(e) => onChange(idx, 'team2', e.target.value)}
              displayEmpty
              sx={{ width: '100%' }}>
              <MenuItem value="">Select Team 2</MenuItem>
              <MenuItem value="walkOver">
                <Chip label="Walk Over" color="warning" size="small" />
              </MenuItem>
              <MenuItem value="TBD">
                <Chip label="To Be Determined" color="info" size="small" />
              </MenuItem>
              {teams
                .filter(
                  (t) =>
                    t.id === match.team2.id ||
                    (!matchUps.some(
                      (m, i) => i !== idx && (m.team1.id === t.id || m.team2.id === t.id)
                    ) &&
                      t.id !== match.team1.id)
                )
                .map((t) => (
                  <MenuItem key={t.id} value={t}>
                    {t.name}
                  </MenuItem>
                ))}
            </MuiSelect>
          </TableCell>
          <TableCell sx={{ width: '10%', textAlign: 'center' }}>
            <IconButton
              aria-label="remove"
              color="error"
              onClick={() => onRemoveMatch(idx)}
              size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
      ))}
      <TableRow
        sx={{
          '& td': { borderBottom: 'none' }
        }}>
        <TableCell colSpan={4} align="right">
          <Button variant="outlined" onClick={onAddMatch}>
            Add
          </Button>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

// Generate tournament bracket from players.
const GenerateBracketFromPlayers = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const generating = useSelector((state) => state.bracket.generating);
  const event = useSelector((state) => state.event.event);
  const eventId = useParams().eventId;

  const [matchUps, setMatchUps] = useState([]);

  const addMatch = () => {
    setMatchUps([...matchUps, { team1: '', team2: '' }]);
  };

  const handleMatchChange = (idx, field, value) => {
    const updated = matchUps.map((m, i) => (i === idx ? { ...m, [field]: value } : m));
    setMatchUps(updated);
  };

  const removeMatch = (idx) => {
    setMatchUps(matchUps.filter((_, i) => i !== idx));
  };

  //   () => {
  //   const players = event.players;
  //   const matchUps = [];
  //   for (let i = 0; i < players.length; i += 2) {
  //     matchUps.push({
  //       player1: players[i],
  //       player2: players[i + 1]
  //     });
  //   }
  //   return matchUps;
  // })();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setError,
    clearErrors
  } = useForm({
    resolver: yupResolver(schema)
  });

  const watchRoundType = watch('type');

  useEffect(() => {
    if (watchRoundType) {
      //Check if the sections matches the type of rounds. If not, show an error message. 16 sections for Last16, 8 sections for Last8, 4 sections for Last4
      //checkSections(watchRoundType);
    }
  }, [watchRoundType]);

  const onSubmit = (data) => {
    data.eventId = eventId;
    data.matches = matchUps;
    dispatch(generateBracket(data))
      .unwrap()
      .then((result) => {
        onSuccess();
      });
  };

  return (
    <Box>
      <Form>
        <Input
          name="name"
          label="Bracket Name"
          error={errors?.name}
          helperText={errors?.name?.message}
          {...register('name')}
        />
        <Select
          name="type"
          label="Bracket Type"
          control={control}
          defaultValue="Last16"
          error={errors?.type}
          helperText={errors?.type?.message}>
          <MenuItem value="Last64">Last 64</MenuItem>
          <MenuItem value="Last32">Last 32</MenuItem>
          <MenuItem value="Last16">Last 16</MenuItem>
          <MenuItem value="Last8">Last 8</MenuItem>
          <MenuItem value="Last4">Last 4</MenuItem>
          <MenuItem value="Last2">Last 2</MenuItem>
        </Select>
        <Box m={2} />
        <Typography variant="h6">Initial Setup</Typography>
        <PlayerDropdown
          teams={event.teams}
          matchUps={matchUps}
          onChange={handleMatchChange}
          onAddMatch={addMatch}
          onRemoveMatch={removeMatch}
        />
        {/* Table Sections, VS Section */}
        {/* <Table size="small" sx={{ my: 2 }}>
          <TableBody>
            {sections.map((section) => (
              <TableRow key={section.id}>
                <TableCell>{section.name}</TableCell>
                <TableCell>VS</TableCell>
                <TableCell>{getVS(section)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table> */}

        {generating ? (
          <CircularProgress color="yellow.main" />
        ) : (
          <YellowButton onClick={handleSubmit(onSubmit)}>Generate Bracket</YellowButton>
        )}
      </Form>
    </Box>
  );
};

export default GenerateBracketFromPlayers;
